import React, { useEffect, useState } from "react";
import { Panel } from "../components/Panel";
import { usePlayer } from "../context/PlayerContext";
import { api, apiErr } from "../api/client";
import { Coins, Check, Lock, Zap, ShieldOff, Loader2, CreditCard, Sparkles, ReceiptText, Clock, Flame, Crown, Eye, X } from "lucide-react";
import { Sound } from "../game/audio";
import { VehiclePreview } from "../components/VehiclePreview";
import { CosmeticIcon } from "../components/CosmeticIcon";

const skinTheme = (id) => (id || "").replace("skin_", "");

const RARITY_COLOR = { common: "#22D3EE", rare: "#34D399", epic: "#A78BFA", legendary: "#FFD700" };
const rarityOf = (c) => {
  const p = c.price || 0;
  if (p <= 900) return "common";
  if (p <= 1500) return "rare";
  if (p <= 2200) return "epic";
  return "legendary";
};

const TYPE_LABEL = { skin: "Skins", wheel: "Wheels", trail: "Trails", paint: "Paint", smash: "Smash Effects", boost: "Boost Effects", title: "Titles" };

const fmtCountdown = (s) => {
  if (s <= 0) return "0h";
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

export default function Shop({ onClose }) {
  const { profile, applyUser } = usePlayer();
  const [catalog, setCatalog] = useState([]);
  const [store, setStore] = useState(null);
  const [featured, setFeatured] = useState(null);
  const [featuredLeft, setFeaturedLeft] = useState(0);
  const [buying, setBuying] = useState(null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => { api.shop().then(({ data }) => setCatalog(data.catalog)).catch((e) => setErr(apiErr(e))); }, []);
  useEffect(() => { api.paymentProducts().then(({ data }) => setStore(data)).catch(() => {}); }, []);
  useEffect(() => { api.shopFeatured().then(({ data }) => { setFeatured(data.featured); setFeaturedLeft(data.secondsLeft || 0); }).catch(() => {}); }, []);
  useEffect(() => { const t = setInterval(() => setFeaturedLeft((s) => Math.max(0, s - 1)), 1000); return () => clearInterval(t); }, []);

  const toggleHistory = () => {
    setShowHistory((v) => !v);
    if (!history) api.paymentHistory().then(({ data }) => setHistory(data.history)).catch(() => setHistory([]));
  };

  const owned = profile.ownedCosmetics || [];
  const buy = async (id) => {
    setBusy(id); setErr(null);
    try { const { data } = await api.buy(id); applyUser(data.user); Sound.pickup(); }
    catch (e) { setErr(apiErr(e)); }
    finally { setBusy(null); }
  };

  const purchase = async (lk) => {
    setBuying(lk); setErr(null);
    try { const { data } = await api.checkout(lk); window.location.href = data.checkout_url; }
    catch (e) { setErr(apiErr(e, "Could not start checkout.")); setBuying(null); }
  };

  const groups = Object.keys(TYPE_LABEL);
  return (
    <Panel title="Shop" onClose={onClose} wide>
      <div className="flex items-center justify-between mb-3">
        <div className="text-slate-400 text-sm">Cosmetics only — never pay-to-win.</div>
        <div className="card px-3 py-1.5 flex items-center gap-1.5"><Coins size={16} className="text-amber-400" /><span className="font-black text-white" data-testid="shop-coins">{profile.coins.toLocaleString()}</span></div>
      </div>
      {err && <div className="text-red-400 text-xs mb-2" data-testid="shop-error">{err}</div>}

      {/* Coins & Store (real-money via Stripe) */}
      {store && (
        <div className="mb-5" data-testid="store-section">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-widest text-amber-400 font-bold flex items-center gap-1"><CreditCard size={14} /> Coins & Store</div>
            <button onClick={toggleHistory} className="text-[10px] uppercase font-bold text-cyan-400 flex items-center gap-1 hover:text-cyan-300" data-testid="purchase-history-btn"><ReceiptText size={12} /> Receipts</button>
          </div>

          {showHistory && (
            <div className="card p-3 mb-3" data-testid="purchase-history">
              <div className="text-[11px] uppercase tracking-wide text-slate-400 font-bold mb-2">Purchase History</div>
              {history === null && <div className="text-slate-500 text-sm py-2 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading...</div>}
              {history && history.length === 0 && <div className="text-slate-500 text-sm py-2" data-testid="history-empty">No purchases yet.</div>}
              {history && history.map((h, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-800/60 last:border-0 text-sm">
                  <div>
                    <div className="text-white font-semibold">{h.name}</div>
                    <div className="text-[10px] text-slate-500">{h.date ? new Date(h.date).toLocaleDateString() : ""}</div>
                  </div>
                  <div className="text-amber-400 font-mono font-bold">${h.amount.toFixed(2)}</div>
                </div>
              ))}
            </div>
          )}

          {store.bundle && (
            <div className="card p-3 mb-3 border-2 border-amber-500/60 relative overflow-hidden glow-pulse" data-testid="starter-bundle">
              <div className="absolute top-0 right-0 bg-amber-500 text-black text-[10px] font-black px-2 py-0.5 rounded-bl-lg">{store.bundle.tag}</div>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-600 flex items-center justify-center"><Sparkles size={26} className="text-black" /></div>
                  <div>
                    <div className="text-white font-black">{store.bundle.name}</div>
                    <div className="text-xs text-slate-300">{store.bundle.coins.toLocaleString()} Coins + {store.bundle.cosmeticName}</div>
                    <div className="text-[10px] text-slate-500"><span className="line-through">${store.bundle.value.toFixed(2)}</span> value • one-time only</div>
                  </div>
                </div>
                <button disabled={buying === "starter_bundle"} onClick={() => purchase("starter_bundle")} className="btn-primary py-2 px-4 flex items-center gap-1 shrink-0" data-testid="buy-bundle">
                  {buying === "starter_bundle" ? <Loader2 size={14} className="animate-spin" /> : `$${store.bundle.price.toFixed(2)}`}
                </button>
              </div>
            </div>
          )}

          {(store.vip || store.season) && (
            <div className="grid sm:grid-cols-2 gap-2 mb-3" data-testid="memberships">
              {store.vip && (
                <div className="card p-3 border-2 border-fuchsia-500/50 relative overflow-hidden" data-testid="vip-product">
                  <div className="absolute top-0 right-0 bg-fuchsia-500 text-black text-[10px] font-black px-2 py-0.5 rounded-bl-lg">{store.vip.tag}</div>
                  <div className="flex items-center gap-2 mb-1.5"><Crown size={20} className="text-fuchsia-400" /><div className="text-white font-black">{store.vip.name}</div></div>
                  <ul className="text-[11px] text-slate-300 space-y-0.5 mb-2.5">{store.vip.perks.map((p, i) => <li key={i} className="flex items-center gap-1"><Check size={11} className="text-green-400 shrink-0" /> {p}</li>)}</ul>
                  {store.vip.owned ? <span className="text-green-400 text-xs font-bold flex items-center gap-1" data-testid="vip-owned"><Check size={14} /> VIP Active</span> :
                    <button disabled={buying === "vip_pass"} onClick={() => purchase("vip_pass")} className="btn-vip w-full py-2 text-sm flex items-center justify-center gap-1" data-testid="buy-vip">{buying === "vip_pass" ? <Loader2 size={14} className="animate-spin" /> : `Get VIP — $${store.vip.price.toFixed(2)}`}</button>}
                </div>
              )}
              {store.season && (
                <div className="card p-3 border-2 border-amber-500/50 relative overflow-hidden" data-testid="season-product">
                  <div className="absolute top-0 right-0 bg-amber-500 text-black text-[10px] font-black px-2 py-0.5 rounded-bl-lg">{store.season.tag}</div>
                  <div className="flex items-center gap-2 mb-1.5"><Sparkles size={20} className="text-amber-400" /><div className="text-white font-black">{store.season.name}</div></div>
                  <ul className="text-[11px] text-slate-300 space-y-0.5 mb-2.5">{store.season.perks.map((p, i) => <li key={i} className="flex items-center gap-1"><Check size={11} className="text-green-400 shrink-0" /> {p}</li>)}</ul>
                  {store.season.owned ? <span className="text-green-400 text-xs font-bold flex items-center gap-1" data-testid="season-owned"><Check size={14} /> Unlocked</span> :
                    <button disabled={buying === "season_pass"} onClick={() => purchase("season_pass")} className="btn-gold w-full py-2 text-sm flex items-center justify-center gap-1" data-testid="buy-season">{buying === "season_pass" ? <Loader2 size={14} className="animate-spin" /> : `Unlock — $${store.season.price.toFixed(2)}`}</button>}
                </div>
              )}
            </div>
          )}

          {(store.legendary || store.vault) && (
            <div className="grid sm:grid-cols-2 gap-2 mb-3" data-testid="premium-bundles">
              {store.legendary && (
                <div className="card p-3 border-2 border-violet-500/50 relative overflow-hidden" data-testid="legendary-product">
                  <div className="absolute top-0 right-0 bg-violet-500 text-black text-[10px] font-black px-2 py-0.5 rounded-bl-lg">{store.legendary.tag}</div>
                  <div className="flex items-center gap-2 mb-1.5"><Sparkles size={20} className="text-violet-400" /><div className="text-white font-black">{store.legendary.name}</div></div>
                  <ul className="text-[11px] text-slate-300 space-y-0.5 mb-2.5">{store.legendary.perks.map((p, i) => <li key={i} className="flex items-center gap-1"><Check size={11} className="text-green-400 shrink-0" /> {p}</li>)}</ul>
                  {store.legendary.owned ? <span className="text-green-400 text-xs font-bold flex items-center gap-1" data-testid="legendary-owned"><Check size={14} /> Unlocked</span> :
                    <button disabled={buying === "legendary_bundle"} onClick={() => purchase("legendary_bundle")} className="btn-vip w-full py-2 text-sm flex items-center justify-center gap-1" data-testid="buy-legendary">{buying === "legendary_bundle" ? <Loader2 size={14} className="animate-spin" /> : `Unlock — $${store.legendary.price.toFixed(2)}`}</button>}
                </div>
              )}
              {store.vault && (
                <div className="card p-3 border-2 border-amber-400/60 relative overflow-hidden glow-pulse" data-testid="vault-product">
                  <div className="absolute top-0 right-0 bg-amber-400 text-black text-[10px] font-black px-2 py-0.5 rounded-bl-lg">{store.vault.tag}</div>
                  <div className="flex items-center gap-2 mb-1.5"><Crown size={20} className="text-amber-300" /><div className="text-white font-black">{store.vault.name}</div></div>
                  <ul className="text-[11px] text-slate-300 space-y-0.5 mb-2.5">{store.vault.perks.map((p, i) => <li key={i} className="flex items-center gap-1"><Check size={11} className="text-green-400 shrink-0" /> {p}</li>)}</ul>
                  {store.vault.owned ? <span className="text-green-400 text-xs font-bold flex items-center gap-1" data-testid="vault-owned"><Check size={14} /> Unlocked</span> :
                    <button disabled={buying === "gold_vault"} onClick={() => purchase("gold_vault")} className="btn-gold w-full py-2 text-sm flex items-center justify-center gap-1" data-testid="buy-vault">{buying === "gold_vault" ? <Loader2 size={14} className="animate-spin" /> : `Unlock — $${store.vault.price.toFixed(2)}`}</button>}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {store.products.filter((p) => p.type === "coins").map((p) => (
              <div key={p.lookup_key} className="card p-3 flex flex-col items-center gap-1 relative" data-testid={`coinpack-${p.lookup_key}`}>
                {p.tag && <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] font-black uppercase bg-amber-500 text-black px-2 py-0.5 rounded-full whitespace-nowrap">{p.tag}</span>}
                <Coins size={26} className="text-amber-400 mt-1" />
                <div className="text-white font-black">{p.coins.toLocaleString()}</div>
                <div className="text-[10px] text-slate-400">{p.name}</div>
                <button disabled={buying === p.lookup_key} onClick={() => purchase(p.lookup_key)}
                  className="btn-primary w-full py-1.5 text-xs flex items-center justify-center gap-1 mt-1" data-testid={`buy-coins-${p.lookup_key}`}>
                  {buying === p.lookup_key ? <Loader2 size={12} className="animate-spin" /> : `$${p.price.toFixed(2)}`}
                </button>
              </div>
            ))}
          </div>
          {store.products.filter((p) => p.type === "remove_ads").map((p) => (
            <div key={p.lookup_key} className="card p-3 mt-2 flex items-center justify-between" data-testid="remove-ads-product">
              <div className="flex items-center gap-2">
                <ShieldOff size={20} className="text-cyan-400" />
                <div>
                  <div className="text-white font-bold text-sm">{p.name}</div>
                  <div className="text-[10px] text-slate-400">One-time • cosmetic only, no gameplay advantage</div>
                </div>
              </div>
              {store.adsRemoved ? (
                <span className="text-green-400 text-xs font-bold flex items-center gap-1"><Check size={14} /> Owned</span>
              ) : (
                <button disabled={buying === p.lookup_key} onClick={() => purchase(p.lookup_key)}
                  className="btn-primary py-1.5 px-4 text-xs flex items-center gap-1" data-testid="buy-remove-ads">
                  {buying === p.lookup_key ? <Loader2 size={12} className="animate-spin" /> : `$${p.price.toFixed(2)}`}
                </button>
              )}
            </div>
          ))}
          <div className="text-[10px] text-slate-500 mt-2">Secure checkout by Stripe. Test mode — use card 4242 4242 4242 4242.</div>
        </div>
      )}

      <div className="text-xs uppercase tracking-widest text-cyan-400 font-bold mb-2 border-t border-slate-800 pt-4">Cosmetics</div>

      {featured && (
        <div className="card p-3 mb-4 border-2 border-fuchsia-500/60 relative overflow-hidden" data-testid="featured-item">
          <div className="absolute top-0 right-0 bg-fuchsia-500 text-black text-[10px] font-black px-2 py-0.5 rounded-bl-lg flex items-center gap-1"><Flame size={11} /> LIMITED</div>
          <div className="text-[10px] uppercase tracking-widest text-fuchsia-400 font-bold mb-2 flex items-center gap-1"><Clock size={12} /> Featured this week · ends in {fmtCountdown(featuredLeft)}</div>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              {featured.type === "skin" ? (
                <div className="w-14 h-14 flex items-center justify-center shrink-0" data-testid={`skin-preview-${featured.id}`}>
                  <VehiclePreview tier={0} theme={skinTheme(featured.id)} size={56} spin={false} />
                </div>
              ) : (
                <div className="w-14 h-14 flex items-center justify-center shrink-0">
                  <CosmeticIcon type={featured.type} color={featured.color} size={52} />
                </div>
              )}
              <div>
                <div className="text-white font-black">{featured.name}</div>
                <div className="text-xs text-slate-400 capitalize">{TYPE_LABEL[featured.type] || featured.type}</div>
                <div className="text-[10px] text-fuchsia-300 font-bold">30% OFF — only while featured</div>
              </div>
            </div>
            {owned.includes(featured.id) ? (
              <span className="text-green-400 text-xs font-bold flex items-center gap-1 shrink-0"><Check size={14} /> Owned</span>
            ) : (
              <button disabled={busy === featured.id || profile.coins < featured.discountPrice} onClick={() => buy(featured.id)}
                className="btn-primary py-2 px-4 flex items-center gap-1 shrink-0 disabled:opacity-50" data-testid="buy-featured">
                {busy === featured.id ? <Loader2 size={14} className="animate-spin" /> : <><Coins size={14} /> {featured.discountPrice} <span className="line-through text-[10px] opacity-60 ml-1">{featured.price}</span></>}
              </button>
            )}
          </div>
        </div>
      )}

      {groups.map((g) => (
        <div key={g} className="mb-4">
          <div className="text-xs uppercase tracking-widest text-amber-400 font-bold mb-2">{TYPE_LABEL[g]}</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {catalog.filter((c) => c.type === g && !c.exclusive && !c.featured).map((c) => {
              const has = owned.includes(c.id);
              return (
                <div key={c.id} className="card cos-tile p-3 flex flex-col items-center gap-2" data-testid={`shop-item-${c.id}`}>
                  <button onClick={() => setPreview(c)} className="w-14 h-14 flex items-center justify-center relative group" data-testid={`preview-${c.id}`} title="Tap to preview">
                    {c.type === "skin" ? (
                      <div className="w-14 h-14 flex items-center justify-center" data-testid={`skin-preview-${c.id}`}>
                        <VehiclePreview tier={0} theme={skinTheme(c.id)} size={56} spin={false} />
                      </div>
                    ) : (
                      <CosmeticIcon type={c.type} color={c.color} size={48} />
                    )}
                    <span className="absolute -bottom-1 -right-1 bg-slate-900 border border-cyan-500/50 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><Eye size={11} className="text-cyan-400" /></span>
                  </button>
                  <div className="text-xs text-white font-bold text-center leading-tight">{c.name}</div>
                  <span className="text-[9px] uppercase font-black tracking-wide" style={{ color: RARITY_COLOR[rarityOf(c)] }}>{rarityOf(c)}</span>
                  {has ? (
                    <div className="text-green-400 text-xs font-bold flex items-center gap-1"><Check size={12} /> Owned</div>
                  ) : c.price === 0 ? (
                    <div className="text-slate-500 text-xs">Free</div>
                  ) : (
                    <button disabled={busy === c.id || profile.coins < c.price} onClick={() => buy(c.id)}
                      className="btn-primary w-full py-1.5 text-xs flex items-center justify-center gap-1 disabled:opacity-50" data-testid={`buy-${c.id}`}>
                      {profile.coins < c.price && <Lock size={11} />} <Coins size={12} /> {c.price}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* ---------- COSMETIC PREVIEW SPIN ---------- */}
      {preview && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4" onClick={() => setPreview(null)} data-testid="preview-modal">
          <div className="card p-6 max-w-sm w-full relative flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setPreview(null)} className="absolute top-3 right-3 text-slate-400 hover:text-white" data-testid="preview-close"><X size={20} /></button>
            <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-bold mb-1">{TYPE_LABEL[preview.type] || preview.type}</div>
            <div className="font-head font-black text-2xl text-white mb-3">{preview.name}</div>
            <div className="relative flex items-center justify-center" style={{ width: 240, height: 240 }}>
              {preview.type === "skin" ? (
                <VehiclePreview tier={0} theme={skinTheme(preview.id)} size={240} spin={true} />
              ) : (
                <div className="spin-slow" style={{ filter: `drop-shadow(0 0 24px ${preview.color})` }}>
                  <CosmeticIcon type={preview.type} color={preview.color} size={180} />
                </div>
              )}
            </div>
            <div className="mt-4 w-full">
              {owned.includes(preview.id) ? (
                <div className="text-green-400 text-sm font-bold flex items-center justify-center gap-1"><Check size={16} /> Owned</div>
              ) : preview.price === 0 ? (
                <div className="text-slate-400 text-sm text-center">Free — equip it in the Garage</div>
              ) : (
                <button disabled={busy === preview.id || profile.coins < preview.price} onClick={() => buy(preview.id)}
                  className="btn-primary w-full py-3 flex items-center justify-center gap-1 disabled:opacity-50" data-testid={`preview-buy-${preview.id}`}>
                  {busy === preview.id ? <Loader2 size={16} className="animate-spin" /> : <>{profile.coins < preview.price && <Lock size={13} />} <Coins size={15} /> {preview.price}</>}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </Panel>
  );
}
